import { useState } from 'react';

import { actionLog } from 'services/actionlog.service';
import {
  deleteFeedbackByQuery,
  getNextManagers,
  getServerTime,
  getTasksManagerNormal,
  updateTask,
} from 'services/dashboard.service';
import { User } from 'types/user';
import { CheckManagerType } from 'utils/strings';

import { NormalRuleContext, userCodEtapa } from './context';
import { makeGuardBatch, makeGuardTask } from './concurrency';
import { NormalQuotas, selectCascadeReversal, selectCascadeTargets } from './quotas';
import { canApproveParticipantsCount, canPromote, canReset, getTaskStepLocal } from './rules';

// Orquestração das ações do fluxo Normal — ports 1:1 dos handlers do monólito
// (index.tsx:2288-3112 e 2465-2656). Este hook NÃO contém regra de domínio própria:
// gates vêm de rules.ts, cascatas de quotas.ts e concorrência de concurrency.ts.
//
// Diferenças deliberadas vs monólito (universo Normal, tasks com mad !== true):
// - SEM o bloco isDuo de swap da dupla MAD (index.tsx:2296-2335);
// - SEM o guard batch da bonificação MAD (`else if (isMAD(item))`, index.tsx:2845-2858);
// - pit stop do handlePromote SEM o termo `!isMAD(item)` (sempre true aqui);
// - SEM os pushes otimistas de estado (setValidTasks etc.): as cotas são derivadas de
//   `data` a cada render pela página (computeQuotas) e recarregadas no finally.

export interface NormalActionsDeps {
  // Contexto de regras do fluxo — a página DEVE construí-lo com os valores de `quotas`
  // (promotedTasks/maxPromotedTasks etc.), como o monólito fazia com os estados.
  ctx: NormalRuleContext;
  data: any[] | undefined;
  filteredData: any[] | undefined;
  // loadData do monólito com os parâmetros da fase fechados: com taskId devolve a task
  // fresca; sem argumento recarrega a lista inteira.
  reload: (taskId?: string) => Promise<any>;
  setPageLoading: (loading: boolean) => void;
  // setShowConcurrentActionModal do monólito.
  showConcurrentModal: (message: string) => void;
  quotas: NormalQuotas;
  bonus: any;
  user: User | undefined;
  isVacationManager: boolean;
}

export function useNormalActions(deps: NormalActionsDeps) {
  const { ctx, filteredData, reload, setPageLoading, showConcurrentModal, quotas, bonus, user, isVacationManager } = deps;

  const playerInfo: any = ctx.playerInfo;
  const userInfo: any = ctx.userInfo;
  const activeStageObj: any = ctx.activeStageObj;
  const { realDatetime, getActiveEvaluationStep } = ctx;

  const [selectedGestor, setSelectedGestor] = useState<any>();
  const [functionGestor, setFunctionGestor] = useState<string>('');
  const [getAction, setGetAction] = useState<any>(false);
  const [modalCod5, setModalCod5] = useState<any>(undefined);

  // Port do memo isDirectManager (index.tsx:139-147).
  const isDirectManager =
    isVacationManager &&
    userInfo &&
    playerInfo &&
    (userInfo?._id === playerInfo?.gestor ||
      (Array.isArray(userInfo?.mad_dupla) && userInfo?.mad_dupla?.some((md: any) => md._id && md._id === playerInfo?.gestor)));

  const boundCanReset = (task: any) => canReset(ctx, task);

  const guardTaskAgainstConcurrentUpdate = makeGuardTask({ reload, onConflict: showConcurrentModal });

  // CON-07: o guard em lote revalida contra o MESMO aggregate do fluxo (tasks_manager_normal).
  const guardBatchAgainstConcurrentUpdate = makeGuardBatch({
    fetchAll: () =>
      getTasksManagerNormal(
        ctx.internalUser?.extra?.id,
        activeStageObj?.date_start,
        activeStageObj?.date_finish,
        activeStageObj?.step,
      ),
    onConflict: showConcurrentModal,
  });

  // Port 1:1 de index.tsx:2288-2463, SEM o bloco isDuo (2296-2335) — fluxo Normal não
  // tem swap de dupla MAD.
  async function updateAllTasks(action: string, nObj: any, oldObj?: any) {
    let taskId;

    try {
      setPageLoading(true);

      const newObj = JSON.parse(JSON.stringify(nObj));

      // Auto-aprovação da quantidade de participantes ao apreciar: quando um gestor com nível
      // suficiente para autorizar a quantidade desta realização faz uma apreciação "para frente"
      // (validar, finalizar, bonificar, promover), a quantidade é aprovada automaticamente.
      // PART-02: "Devolver" (reject) e "reset" não aprovam.
      if (!!action.match(/(accept|stop|double|award|promote)/g) && canApproveParticipantsCount(playerInfo, nObj)) {
        newObj.participants_approval = { ...(newObj.participants_approval || {}), status: 'approved' };
        newObj.maxPlayersNumber = Array.isArray(newObj.team) ? newObj.team.length : newObj.maxPlayersNumber;
      }

      if (!!action.match(/(accept|stop|reject|double|award)/g)) {
        taskId = newObj._id;
        await updateTask(newObj);
        return true;
      } else if (!!action.match(/(promote|reset)/g)) {
        if (action === 'promote') {
          if (newObj.prosseguir_idGestor === playerInfo._id) {
            taskId = newObj._id;
            await updateTask(newObj);
            return true;
          } else {
            const _promotedTasks = quotas.promotedTasks.filter((x: any) => x._id !== newObj._id);
            _promotedTasks.push(newObj);

            const cotaReached = _promotedTasks.length >= quotas.maxPromotedTasks;

            // Ao fechar a cota de promoção, as demais realizações bonificadas (dobrado) viram
            // concluidoAuto. Essa decisão depende da lista inteira do gestor, então validamos
            // contra os dados frescos do servidor: se outro gestor bonificou/promoveu algo que
            // ainda não está nesta tela, cancelamos a ação e recarregamos — mesma filosofia do
            // guardTaskAgainstConcurrentUpdate, porém para todas as realizações.
            if (cotaReached) {
              const guard = await guardBatchAgainstConcurrentUpdate(ctx, filteredData as any[]);
              if (!guard.ok) {
                return null;
              }
            }

            // CAS-01 — cascata direta (filtro em quotas.selectCascadeTargets).
            const tasks = selectCascadeTargets(ctx, filteredData as any[], newObj, boundCanReset);

            if (tasks.length) {
              await actionLog('me', 'change_task_status', {
                tasks: tasks.map((x: any) => {
                  return {
                    taskId: x._id,
                    status: 'concluidoAuto',
                  };
                }),
              });
            }

            if (quotas.promotedTasks.length >= quotas.maxPromotedTasks) {
              await actionLog('me', 'active_bonus', { task: newObj._id, stage: newObj.stageNumber });
            }

            await updateTask(newObj);
            return true;
          }
        } else {
          if (newObj.prosseguir_idGestor === playerInfo._id) {
            taskId = newObj._id;
            await updateTask(newObj);
            return true;
          } else {
            // CAS-04 — cascata reversa (filtro em quotas.selectCascadeReversal).
            const tasks = selectCascadeReversal(ctx, filteredData as any[], newObj, oldObj, boundCanReset);

            if (
              filteredData?.find(e => e._id === bonus?.taskId && e.stageNumber === bonus.stage) &&
              (oldObj?.status === 'promovido' || oldObj?.status === 'selo')
            ) {
              await actionLog('me', 'remove_action', { id: bonus?.action });
            } else if (tasks.length) {
              await actionLog('me', 'change_task_status', {
                tasks: tasks.map((x: any) => {
                  return {
                    taskId: x._id,
                    status: 'dobrado',
                  };
                }),
              });
            }

            await updateTask(newObj);
            return true;
          }
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      await reload(taskId);
      setPageLoading(false);
    }

    return null;
  }

  // Port 1:1 de index.tsx:2465-2656. CAS-05: o guard em lote roda ANTES de qualquer
  // mutação (deleteFeedbackByQuery) — havendo divergência, cancela sem efeitos colaterais.
  async function handleReset(task: any) {
    if (!canReset(ctx, task)) return false;

    const newObj = await guardTaskAgainstConcurrentUpdate(task);
    if (!newObj) return false;

    // Resetar uma realização que estava promovida/selo abre uma vaga de promoção e faz as
    // bonificadas que viraram concluidoAuto voltarem a dobrado (cascata reversa), além de
    // apagar feedbacks. Como esses efeitos (deleteFeedbackByQuery) ocorrem antes do
    // updateAllTasks, validamos a lista inteira contra o servidor ANTES de qualquer
    // mutação: havendo divergência, cancelamos e recarregamos sem efeitos colaterais.
    if (task.status === 'promovido' || task.status === 'selo') {
      const guard = await guardBatchAgainstConcurrentUpdate(ctx, filteredData as any[]);
      if (!guard.ok) {
        void reload();
        return false;
      }
    }

    delete newObj.evaluator;

    if (
      (String(task.original_manager) === String(playerInfo._id) ||
        (Array.isArray(playerInfo?.mad_dupla) &&
          playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
      userCodEtapa(ctx, null, !!task.mad) === 4 &&
      activeStageObj &&
      activeStageObj.date_evaluation_step4_start &&
      realDatetime < activeStageObj.date_evaluation_step4_start
    ) {
      delete newObj.stageNumber;
      delete newObj.multiplicador;
      delete newObj.prosseguir_gst;
      delete newObj.prosseguir_idGestor;
      delete newObj.prosseguir_who_approved;

      newObj.status = 'aguardando';

      await deleteFeedbackByQuery(`taskId:'${newObj._id}'`);
    } else if (
      (String(task.original_manager) === String(playerInfo._id) ||
        (Array.isArray(playerInfo?.mad_dupla) &&
          playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
      userCodEtapa(ctx, null, !!task.mad) === 5
    ) {
      delete newObj.stageNumber;
      delete newObj.multiplicador;
      delete newObj.prosseguir_gst;
      delete newObj.prosseguir_idGestor;
      delete newObj.prosseguir_who_approved;

      newObj.status = 'aguardando';

      await deleteFeedbackByQuery(`taskId:'${newObj._id}'`);
    } else {
      const _getActiveEvaluationStep =
        isDirectManager && getTaskStepLocal(newObj) && getTaskStepLocal(newObj) !== getActiveEvaluationStep
          ? getTaskStepLocal(newObj)
          : getActiveEvaluationStep;

      if (_getActiveEvaluationStep === 2) {
        delete newObj.stageNumber;
        delete newObj.multiplicador;
        delete newObj.prosseguir_gst;
        delete newObj.prosseguir_idGestor;
        delete newObj.prosseguir_who_approved;

        newObj.status = 'aguardando';

        await deleteFeedbackByQuery(`taskId:'${newObj._id}'`);
      } else {
        const playerId =
          Array.isArray(task.evaluator) && task.evaluator.includes(String(playerInfo._id))
            ? String(playerInfo._id)
            : Array.isArray(playerInfo.mad_dupla) &&
                Array.isArray(task.evaluator) &&
                playerInfo.mad_dupla.some((md: any) => task.evaluator.includes(String(md._id)))
              ? String(playerInfo.mad_dupla.find((md: any) => task.evaluator.includes(String(md._id)))._id)
              : null;

        if (playerId) {
          newObj.evaluator = [];

          for (let index = 0; index < task.evaluator.length; index++) {
            const id = task.evaluator[index];

            newObj.evaluator.push(id);

            if (id === playerId) {
              break;
            }
          }
        }

        if (_getActiveEvaluationStep === 3) {
          delete newObj.prosseguir_gst;
          delete newObj.prosseguir_idGestor;
          delete newObj.prosseguir_who_approved;

          newObj.status = 'aprovado';
          newObj.multiplicador = 1;
          newObj.stageNumber = 1;
        } else if (_getActiveEvaluationStep === 4) {
          delete newObj.prosseguir_gst;
          delete newObj.prosseguir_idGestor;
          delete newObj.prosseguir_who_approved;
          delete newObj.prosseguir_managers;

          newObj.status = 'promovido';
          newObj.multiplicador = 2;
          newObj.stageNumber = 2;
        } else if (_getActiveEvaluationStep === 4.1) {
          delete newObj.prosseguir_who_approved;

          newObj.prosseguir_gst = 'next';
          newObj.status = 'promovido';
          newObj.multiplicador = 4;
          newObj.stageNumber = 3;
        } else if (_getActiveEvaluationStep === 5) {
          newObj.status = 'promovido';
          newObj.multiplicador = 4;
          newObj.stageNumber = 3;
        }

        if (!!newObj.feedback?.length) {
          let evaluators: any;

          if (Array.isArray(newObj.evaluator)) {
            evaluators = [newObj.original_manager, ...newObj.evaluator];
          } else {
            evaluators = [newObj.original_manager];
          }

          const evaluatorsWithoutMe = evaluators.filter((x: string) => x !== String(playerInfo._id));

          const feedbacksWithoutMe = newObj.feedback
            .filter((x: any) => {
              if (evaluatorsWithoutMe?.length) {
                const evaluatorId = x.evaluatorId.replace(/F(0+)?/g, '');
                return !evaluatorsWithoutMe.includes(evaluatorId);
              }

              return true;
            })
            .map((x: any) => x._id);

          if (feedbacksWithoutMe.length) {
            const deletedFeedbacks: any = [];

            feedbacksWithoutMe.forEach((feedbackId: string) =>
              deletedFeedbacks.push(deleteFeedbackByQuery(`_id:'${feedbackId}'`)),
            );

            await Promise.all(deletedFeedbacks);
          }
        }
      }
    }

    if (isVacationManager) {
      if (!newObj.substitute_managers) {
        newObj.substitute_managers = [];
      }

      const timestamp = await getServerTime();

      newObj.substitute_managers.push({
        playerId: user?._id,
        playerName: user?.name,
        status: 'reset',
        time: { $date: timestamp },
      });
    }

    await updateAllTasks('reset', newObj, task);
  }

  // Port 1:1 de index.tsx:2657-2723 (sem o push otimista em validTasks).
  async function handleAccept(_item: any, gestor?: string) {
    const item = await guardTaskAgainstConcurrentUpdate(_item);
    if (!item) return;

    const managerTypeResult = CheckManagerType({
      user: { ...playerInfo, cod_etapa: userCodEtapa(ctx, playerInfo, !!item.mad) },
      item,
    });

    const updatedData = { ...item };

    updatedData.status = 'aprovado';
    updatedData.multiplicador = updatedData.multiplicador || 1;
    updatedData.stageNumber = 1;

    if (managerTypeResult === 'equipe') {
      // Com BUG (comentário preservado do original)
      if (!gestor) {
        const nextManagers = await getNextManagers(item._id);
        setSelectedGestor({
          task: item,
          gestor: nextManagers[`nivel1`] && nextManagers[`nivel1`]._id ? nextManagers[`nivel1`]._id : playerInfo.gestor,
          cod: [1, 3],
        });
        setFunctionGestor(`handleAccept`);
        return;
      }

      if (!updatedData.evaluator) {
        updatedData.evaluator = [];
      }

      updatedData.evaluator.push(gestor);
    }

    if (isVacationManager) {
      if (!updatedData.substitute_managers) {
        updatedData.substitute_managers = [];
      }

      const timestamp = await getServerTime();

      updatedData.substitute_managers.push({
        playerId: user?._id,
        playerName: user?.name,
        status: 'aprovado',
        time: { $date: timestamp },
      });
    }

    const updated = await updateAllTasks('accept', updatedData);

    // CON-06: o modal de feedback só deve abrir quando a apreciação foi de fato computada
    // (updateAllTasks retorna truthy). Em caso de abort por concorrência ou erro, não abre.
    if (updated && (!!gestor || managerTypeResult !== 'equipe')) {
      setGetAction({ ...item, fuction: `validation-accept-${item._id}-finish` });
    }
  }

  // Port 1:1 de index.tsx:2725-2764.
  async function handleStop(_item: any) {
    const item = await guardTaskAgainstConcurrentUpdate(_item);
    if (!item) return false;

    const managerTypeResult = CheckManagerType({
      user: { ...playerInfo, cod_etapa: userCodEtapa(ctx, playerInfo, !!item.mad) },
      item,
    });

    const updatedData = { ...item };

    if (
      updatedData.prosseguir_gst &&
      updatedData.prosseguir_gst === 'next' &&
      managerTypeResult === 'pitstop' &&
      updatedData.prosseguir_idGestor === playerInfo._id
    ) {
      updatedData.prosseguir_gst = 'stopped';
    }

    updatedData.status = 'concluido';

    if (isVacationManager) {
      if (!updatedData.substitute_managers) {
        updatedData.substitute_managers = [];
      }

      const timestamp = await getServerTime();

      updatedData.substitute_managers.push({
        playerId: user?._id,
        playerName: user?.name,
        status: 'concluido',
        time: { $date: timestamp },
      });
    }

    const updated = await updateAllTasks('stop', updatedData);
    return !!updated;
  }

  // Port 1:1 de index.tsx:2766-2797 (sem o push otimista em rejectedTasks).
  async function handleReject(_item: any) {
    const item = await guardTaskAgainstConcurrentUpdate(_item);
    if (!item) return;

    const updatedData = { ...item };
    updatedData.status = 'rejeitado';

    // PART-02: devolver NÃO altera a aprovação da quantidade de participantes: uma realização
    // com participantes já aprovados mantém a aprovação. A aprovação só é revista quando a
    // quantidade é aumentada além do número aprovado (tratado na edição em MissaoDestaque/Create).

    if (isVacationManager) {
      if (!updatedData.substitute_managers) {
        updatedData.substitute_managers = [];
      }

      const timestamp = await getServerTime();

      updatedData.substitute_managers.push({
        playerId: user?._id,
        playerName: user?.name,
        status: 'rejeitado',
        time: { $date: timestamp },
      });
    }

    await updateAllTasks('reject', updatedData);
  }

  // Port 1:1 de index.tsx:2799-2877, SEM o guard batch da bonificação MAD
  // (`else if (isMAD(item))`, 2845-2858): no universo Normal as tasks têm mad !== true
  // e a dupla não concorre no mesmo nível.
  async function handleDoubled(_item: any) {
    const item = await guardTaskAgainstConcurrentUpdate(_item);
    if (!item) return false;

    if (String(item.original_manager) === String(playerInfo._id) && userCodEtapa(ctx, null, !!item.mad) === 5) {
      setModalCod5(item);
      return false;
    }

    const multiplicador = typeof item.multiplicador === 'number' ? item.multiplicador * 2 : 1;

    const updatedData = { ...item };
    updatedData.status = 'dobrado';
    updatedData.multiplicador = multiplicador;

    if (!canPromote(ctx, updatedData, item)) {
      updatedData.status = 'concluidoAuto';
    }

    if (isVacationManager) {
      if (!updatedData.substitute_managers) {
        updatedData.substitute_managers = [];
      }

      const timestamp = await getServerTime();

      updatedData.substitute_managers.push({
        playerId: user?._id,
        playerName: user?.name,
        status: 'dobrado',
        time: { $date: timestamp },
      });
    }

    const updated = await updateAllTasks('double', updatedData);
    return !!updated;
  }

  // Port 1:1 de index.tsx:2879-3020 (sem o push otimista em promotedTasks). PIT-01: o
  // pit stop do fluxo Normal permanece; a condição original tinha `&& !isMAD(item)` —
  // no universo Normal isMAD é sempre false, então o termo sai (paridade preservada).
  async function handlePromote(_item: any, gestor?: string, multiplicador?: number) {
    const item = await guardTaskAgainstConcurrentUpdate(_item);
    if (!item) return;

    const managerTypeResult = CheckManagerType({
      user: { ...playerInfo, cod_etapa: userCodEtapa(ctx, playerInfo, !!item.mad) },
      item,
    });

    const nextManagers = await getNextManagers(item._id);

    // Sinaliza se a promoção deve abrir o modal de feedback. CON-06: só dispara depois
    // que o updateAllTasks confirmar que a apreciação foi computada — evita abrir o modal
    // quando a ação é abortada por concorrência (lista desatualizada) ou por erro.
    let shouldShowFeedback = false;

    const updatedData = { ...item };

    let stageNumber = updatedData.stageNumber;

    if (getTaskStepLocal(item) === 3) {
      stageNumber = 2;
    } else if (typeof getTaskStepLocal(item) === 'number' && (getTaskStepLocal(item) as number) >= 4) {
      stageNumber = 3;
    }

    let managerProsseguir: null | string = null;

    if (playerInfo.prosseguir_gst === 1 && stageNumber === 3 && nextManagers && nextManagers.prosseguir) {
      updatedData.prosseguir_gst = 'next';
      updatedData.prosseguir_idGestor = nextManagers.prosseguir._id;

      if (Array.isArray(updatedData.prosseguir_managers)) {
        updatedData.prosseguir_managers.push(updatedData.prosseguir_idGestor);
      } else {
        updatedData.prosseguir_managers = [updatedData.prosseguir_idGestor];
      }
    } else if (
      updatedData.prosseguir_gst &&
      updatedData.prosseguir_gst === 'next' &&
      managerTypeResult === 'pitstop' &&
      stageNumber === 3
    ) {
      managerProsseguir = nextManagers && nextManagers.nivel3 ? nextManagers.nivel3._id : playerInfo.gestor;

      updatedData.prosseguir_gst = 'approved';
      updatedData.prosseguir_who_approved = playerInfo._id;
    }

    updatedData.status = 'promovido';
    updatedData.stageNumber = stageNumber;

    if (!updatedData.evaluator) {
      updatedData.evaluator = [];
    }

    if (managerTypeResult === 'diretoria') {
      updatedData.multiplicador = multiplicador;
      updatedData.status = typeof multiplicador === 'number' && multiplicador >= 16 ? 'selo' : 'concluido';
    } else if (playerInfo.gestor || managerProsseguir) {
      if (typeof managerProsseguir === 'string') {
        updatedData.evaluator.push(managerProsseguir);
        shouldShowFeedback = true;
      } else {
        if (updatedData.prosseguir_gst === 'next') {
          updatedData.evaluator.push(updatedData.prosseguir_idGestor);
          shouldShowFeedback = true;
        } else {
          if (
            userCodEtapa(ctx, null, !!item.mad) === 4 &&
            String(updatedData.original_manager) === String(playerInfo._id) &&
            activeStageObj &&
            activeStageObj.date_evaluation_step4_start &&
            realDatetime < activeStageObj.date_evaluation_step4_start
          ) {
            shouldShowFeedback = true;
          } else if (!gestor) {
            setSelectedGestor({
              task: item,
              cod: stageNumber === 2 ? [4] : [5],
              gestor:
                nextManagers[`nivel${stageNumber}`] && nextManagers[`nivel${stageNumber}`]._id
                  ? nextManagers[`nivel${stageNumber}`]._id
                  : playerInfo.gestor,
            });
            setFunctionGestor(`handlePromote`);
            return;
          }

          updatedData.evaluator.push(gestor);
        }
      }
    }

    if (isVacationManager) {
      if (!updatedData.substitute_managers) {
        updatedData.substitute_managers = [];
      }

      const timestamp = await getServerTime();

      updatedData.substitute_managers.push({
        playerId: user?._id,
        playerName: user?.name,
        status: updatedData.status,
        time: { $date: timestamp },
      });
    }

    // om cod4 promovendo antes da step4: o evaluator não é gravado (o executivo original
    // continua o avaliador corrente).
    if (
      userCodEtapa(ctx, null, !!item.mad) === 4 &&
      String(updatedData.original_manager) === String(playerInfo._id) &&
      getActiveEvaluationStep &&
      getActiveEvaluationStep < 4
    ) {
      delete updatedData.evaluator;
    }

    if (!!gestor) {
      shouldShowFeedback = true;
    }

    const updated = await updateAllTasks('promote', updatedData);

    // CON-06: o modal de feedback só abre quando a apreciação foi de fato computada.
    if (updated && shouldShowFeedback) {
      setGetAction({ ...item, fuction: `validation-promote-${item._id}-finish` });
    }
  }

  // Port 1:1 de index.tsx:3022-3063 (sem os pushes otimistas em uau/mandouBem/valeuTasks).
  async function handleAward(_item: any, badge: 'uau' | 'mandou' | 'valeu') {
    const item = await guardTaskAgainstConcurrentUpdate(_item);
    if (!item) return false;

    const status = badge === 'uau' ? 'selo' : badge;

    const updatedData = { ...item };
    updatedData.status = status;
    updatedData.multiplicador = status === 'selo' ? 16 : status === 'mandou' ? 8 : 5;

    if (isVacationManager) {
      if (!updatedData.substitute_managers) {
        updatedData.substitute_managers = [];
      }

      const timestamp = await getServerTime();

      updatedData.substitute_managers.push({
        playerId: user?._id,
        playerName: user?.name,
        status: status,
        time: { $date: timestamp },
      });
    }

    const updated = await updateAllTasks('award', updatedData);
    return !!updated;
  }

  // Port 1:1 de index.tsx:3066-3111.
  async function handleSubmitCod5(item: any) {
    // `item` chega já editado pela ModalCod5 (status/multiplicador/stageNumber/evaluator
    // refletindo a opção escolhida pelo gestor). A guarda de concorrência precisa comparar
    // o estado ORIGINAL da realização (modalCod5, capturado limpo na abertura da modal)
    // com o servidor — comparar o `item` já editado geraria divergência falsa e abortaria
    // sempre a apreciação. Ao passar, seguimos a atualização com o `item` para preservar a
    // seleção feita na modal.
    const guard = await guardTaskAgainstConcurrentUpdate(modalCod5);
    if (!guard) {
      // Fecha a modal para não cobrir o aviso de que outro gestor já apreciou.
      setModalCod5(undefined);
      return false;
    }

    setModalCod5(undefined);

    let action = 'double';

    if (item.status === 'valeu' || item.status === 'mandou' || item.status === 'selo') {
      action = 'award';
    }

    const updated = await updateAllTasks(action, item);
    if (!updated) return false;

    document.dispatchEvent(
      new CustomEvent('open:modal-feedback', {
        detail: {
          _id: item._id,
        },
      }),
    );
    return true;
  }

  return {
    // estados de orquestração
    selectedGestor,
    setSelectedGestor,
    functionGestor,
    setFunctionGestor,
    getAction,
    setGetAction,
    modalCod5,
    setModalCod5,
    // ações
    updateAllTasks,
    handleAccept,
    handleStop,
    handleReject,
    handleDoubled,
    handlePromote,
    handleAward,
    handleReset,
    handleSubmitCod5,
  };
}
